import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  InputAdornment,
  Stack,
  alpha,
} from '@mui/material';
import {
  EmailOutlined,
  LockOutlined,
  ArrowBack,
  ShieldOutlined,
  MarkEmailReadOutlined,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { requestOtp } from '../store/slices/adminsSlice';
import { otpLogin } from '../store/slices/authSlice';
import { RootState } from '../store';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const emailSchema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
});

const otpSchema = yup.object().shape({
  otp: yup.string().length(6, 'OTP must be 6 digits').required('OTP is required'),
});

type EmailFormData = {
  email: string;
};

type OtpFormData = {
  otp: string;
};

const ACCENT = '#E8A87C';
const ACCENT_DEEP = '#C85A3C';
const INK = '#0B1220';

const LoginPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading: otpLoading, error: otpError } = useSelector((state: RootState) => state.admins);
  const { loading: authLoading, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const loading = otpLoading || authLoading;
  const error = otpError;

  const [activeStep, setActiveStep] = useState(0);
  const [email, setEmail] = useState('');

  const emailForm = useForm<EmailFormData>({
    resolver: yupResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const otpForm = useForm<OtpFormData>({
    resolver: yupResolver(otpSchema),
    defaultValues: { otp: '' },
  });

  React.useEffect(() => {
    if (isAuthenticated) {
      toast.success('Login successful!');
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleEmailSubmit = async (data: EmailFormData) => {
    try {
      const result = await dispatch(requestOtp({ email: data.email }) as any);

      if (result.payload && result.payload.success) {
        setEmail(data.email);
        setActiveStep(1);
        toast.success(result.payload.message || 'OTP sent to your email!');
      } else {
        toast.error(result.payload?.message || 'Failed to send OTP');
      }
    } catch {
      toast.error('Failed to send OTP');
    }
  };

  const handleOtpSubmit = async (data: OtpFormData) => {
    try {
      const result = await dispatch(otpLogin({ email, otp: data.otp }) as any);

      if (result.payload && result.payload.token && result.payload.admin) {
        toast.success(result.payload.message || 'Login successful!');
        navigate('/');
      } else {
        toast.error(result.payload?.message || 'Invalid OTP');
      }
    } catch {
      toast.error('Failed to verify OTP');
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    otpForm.reset();
  };

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: INK,
        overflow: 'hidden',
      }}
    >
      {/* Brand panel */}
      <Box
        sx={{
          position: 'relative',
          flex: { xs: '0 0 auto', md: '1 1 48%' },
          minHeight: { xs: 180, sm: 220, md: '100dvh' },
          display: 'flex',
          alignItems: 'flex-end',
          px: { xs: 3, sm: 4, md: 6 },
          py: { xs: 3, md: 6 },
          background: `
            radial-gradient(ellipse 80% 60% at 20% 20%, ${alpha(ACCENT, 0.35)} 0%, transparent 55%),
            radial-gradient(ellipse 70% 50% at 90% 80%, ${alpha('#4A90A4', 0.28)} 0%, transparent 50%),
            linear-gradient(145deg, #121a2b 0%, #0B1220 45%, #1a1520 100%)
          `,
          borderBottom: { xs: `1px solid ${alpha('#fff', 0.08)}`, md: 'none' },
          borderRight: { md: `1px solid ${alpha('#fff', 0.06)}` },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.35,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 440, width: '100%' }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: { xs: 1.5, md: 3 } }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DEEP} 100%)`,
                boxShadow: `0 8px 24px ${alpha(ACCENT_DEEP, 0.45)}`,
              }}
            >
              <ShieldOutlined sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.03em',
                fontSize: { xs: '1.35rem', md: '1.6rem' },
                color: '#fff',
              }}
            >
              Ibex Games
            </Typography>
          </Stack>

          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 700,
              letterSpacing: '-0.04em',
              fontSize: { sm: '1.75rem', md: '2.35rem' },
              lineHeight: 1.15,
              color: '#fff',
              mb: 1.5,
            }}
          >
            Admin console
          </Typography>
          <Typography
            sx={{
              display: { xs: 'none', md: 'block' },
              color: alpha('#fff', 0.72),
              fontSize: '1rem',
              lineHeight: 1.55,
              maxWidth: 360,
            }}
          >
            Secure OTP sign-in for operators. Manage rooms, deposits, and live games from one place.
          </Typography>
        </Box>
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: { xs: '1 1 auto', md: '1 1 52%' },
          display: 'flex',
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'center',
          px: { xs: 2.5, sm: 4, md: 6 },
          py: { xs: 3, md: 6 },
          pb: { xs: 'max(24px, env(safe-area-inset-bottom))', md: 6 },
          bgcolor: { xs: INK, md: '#0E1524' },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            mx: 'auto',
            p: { xs: 0, sm: 1 },
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.03em',
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              color: '#fff',
              mb: 0.75,
            }}
          >
            {activeStep === 0 ? 'Sign in' : 'Verify OTP'}
          </Typography>
          <Typography sx={{ color: alpha('#fff', 0.6), mb: 3, fontSize: '0.95rem' }}>
            {activeStep === 0
              ? 'Enter your admin email to receive a one-time code.'
              : `We sent a 6-digit code to ${email}`}
          </Typography>

          {/* Compact step pills */}
          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            {['Email', 'OTP'].map((label, idx) => {
              const active = activeStep === idx;
              const done = activeStep > idx;
              return (
                <Box
                  key={label}
                  sx={{
                    flex: 1,
                    py: 1,
                    px: 1.5,
                    borderRadius: 2,
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: active || done ? '#fff' : alpha('#fff', 0.45),
                    bgcolor: active
                      ? alpha(ACCENT_DEEP, 0.35)
                      : done
                        ? alpha(ACCENT, 0.18)
                        : alpha('#fff', 0.04),
                    border: `1px solid ${
                      active ? alpha(ACCENT, 0.55) : done ? alpha(ACCENT, 0.25) : alpha('#fff', 0.08)
                    }`,
                    transition: 'all 0.2s ease',
                  }}
                >
                  {idx + 1}. {label}
                </Box>
              );
            })}
          </Stack>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                bgcolor: alpha('#f44336', 0.12),
                border: `1px solid ${alpha('#f44336', 0.35)}`,
              }}
            >
              {error}
            </Alert>
          )}

          <Box
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: 3,
              bgcolor: alpha('#fff', 0.03),
              border: `1px solid ${alpha('#fff', 0.08)}`,
              boxShadow: `0 20px 50px ${alpha('#000', 0.35)}`,
              backdropFilter: 'blur(8px)',
            }}
          >
            {activeStep === 0 ? (
              <Box component="form" onSubmit={emailForm.handleSubmit(handleEmailSubmit)} noValidate>
                <Controller
                  name="email"
                  control={emailForm.control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Email address"
                      type="email"
                      fullWidth
                      autoComplete="email"
                      autoFocus
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      placeholder="admin@example.com"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlined sx={{ color: alpha('#fff', 0.45) }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        mb: 2.5,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.03),
                        },
                      }}
                    />
                  )}
                />
                <Button
                  type="submit"
                  fullWidth
                  disabled={loading}
                  size="large"
                  sx={{
                    py: 1.4,
                    borderRadius: 2,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '1rem',
                    color: '#fff',
                    background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DEEP} 100%)`,
                    boxShadow: `0 10px 28px ${alpha(ACCENT_DEEP, 0.4)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${ACCENT} 10%, ${ACCENT_DEEP} 90%)`,
                      boxShadow: `0 12px 32px ${alpha(ACCENT_DEEP, 0.5)}`,
                    },
                    '&.Mui-disabled': {
                      color: alpha('#fff', 0.7),
                      background: alpha(ACCENT_DEEP, 0.4),
                    },
                  }}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {loading ? 'Sending code…' : 'Send OTP'}
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={otpForm.handleSubmit(handleOtpSubmit)} noValidate>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha('#4A90A4', 0.12),
                    border: `1px solid ${alpha('#4A90A4', 0.25)}`,
                  }}
                >
                  <MarkEmailReadOutlined sx={{ color: '#7EB8C9', fontSize: 22 }} />
                  <Typography sx={{ color: alpha('#fff', 0.8), fontSize: '0.85rem', lineHeight: 1.4 }}>
                    Check your inbox for the 6-digit code. It expires shortly.
                  </Typography>
                </Stack>

                <Controller
                  name="otp"
                  control={otpForm.control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="One-time password"
                      type="text"
                      fullWidth
                      autoFocus
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      placeholder="••••••"
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                        field.onChange(digits);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined sx={{ color: alpha('#fff', 0.45) }} />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        maxLength: 6,
                        style: {
                          letterSpacing: '0.35em',
                          fontWeight: 700,
                          fontSize: '1.25rem',
                        },
                      }}
                      sx={{
                        mb: 2.5,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: alpha('#fff', 0.03),
                        },
                      }}
                    />
                  )}
                />

                <Stack
                  direction={{ xs: 'column-reverse', sm: 'row' }}
                  spacing={1.25}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={loading}
                    fullWidth
                    startIcon={<ArrowBack />}
                    sx={{
                      py: 1.25,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: alpha('#fff', 0.2),
                      color: alpha('#fff', 0.85),
                      '&:hover': {
                        borderColor: alpha('#fff', 0.4),
                        bgcolor: alpha('#fff', 0.04),
                      },
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    fullWidth
                    disabled={loading}
                    sx={{
                      py: 1.25,
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '1rem',
                      color: '#fff',
                      background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DEEP} 100%)`,
                      boxShadow: `0 10px 28px ${alpha(ACCENT_DEEP, 0.4)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${ACCENT} 10%, ${ACCENT_DEEP} 90%)`,
                      },
                      '&.Mui-disabled': {
                        color: alpha('#fff', 0.7),
                        background: alpha(ACCENT_DEEP, 0.4),
                      },
                    }}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                  >
                    {loading ? 'Verifying…' : 'Verify & continue'}
                  </Button>
                </Stack>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    size="small"
                    disabled={loading || !email}
                    onClick={async () => {
                      try {
                        const result = await dispatch(requestOtp({ email }) as any);
                        if (result.payload && result.payload.success) {
                          toast.success(result.payload.message || 'OTP resent!');
                        } else {
                          toast.error(result.payload?.message || 'Failed to resend OTP');
                        }
                      } catch {
                        toast.error('Failed to resend OTP');
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      px: 1.5,
                      color: ACCENT,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': { bgcolor: alpha(ACCENT, 0.08) },
                    }}
                  >
                    Resend code
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          <Typography
            sx={{
              mt: 3,
              textAlign: 'center',
              color: alpha('#fff', 0.35),
              fontSize: '0.75rem',
            }}
          >
            Authorized staff only · OTP expires after a short time
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
